import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { BaseComponent, PageHeaderComponent } from "@nucleus/ui-base";
import {
  IpBlacklistAdminControllerService,
  IpBlacklistPayload,
} from '@nucleus/security-admin-ui';

/**
 * NUC-87 / NUC-131: admin page for managing the gateway's IP blocklist.
 *
 * <p>Calls the tasksense-user IpBlacklistController which fans the change out
 * via Kafka to the security-gateway. There's no list endpoint yet (gateway
 * diagnostic is pending) — this page is block-form + unblock-form. The
 * actual current-blocklist view lives in the audit-report (every block /
 * unblock is @AuditAction stamped with entity=IpBlacklist).</p>
 */
@Component({
  selector: 'app-ip-blacklist',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatDividerModule,
    PageHeaderComponent,
  ],
  templateUrl: './ip-blacklist.component.html',
  styleUrl: './ip-blacklist.component.css',
})
export class IpBlacklistComponent extends BaseComponent implements OnInit {
  private fb = inject(FormBuilder);
  private api = inject(IpBlacklistAdminControllerService);
  private snack = inject(MatSnackBar);

  blocking = false;
  unblocking = false;

  /** In-component access guard — mirrors the peer admin pages (capabilities,
   *  runners): bounce users who reach the route without the IP-blocklist
   *  authority. The route AuthGuard already gates navigation, but the same
   *  self-gate is kept on every admin screen for defence-in-depth. */
  ngOnInit(): void {
    if (!this.permissionChecker.hasAccessToIpBlocklist()) {
      this.showAccessDeny();
    }
  }

  blockForm = this.fb.nonNullable.group({
    ip: ['', [Validators.required, this.ipValidator()]],
    reason: ['', [Validators.required, Validators.maxLength(200)]],
  });

  unblockForm = this.fb.nonNullable.group({
    ip: ['', [Validators.required, this.ipValidator()]],
  });

  block(): void {
    if (!this.permissionChecker.hasAccessToBlockIp()) { this.showAccessDeny(); return; }
    if (this.blockForm.invalid) {
      this.blockForm.markAllAsTouched();
      return;
    }
    const payload: IpBlacklistPayload = this.blockForm.getRawValue();
    const ref = this.dialog.open(ConfirmIpBlockDialog, { data: payload, width: '400px' });
    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      if (!this.permissionChecker.hasAccessToBlockIp()) { this.showAccessDeny(); return; }
      this.blocking = true;
      this.api.block(payload).subscribe({
        next: () => {
          this.snack.open(`IP ${payload.ip} blocked.`, 'OK', { duration: 4000 });
          this.blockForm.reset();
        },
        error: (err: unknown) => {
          this.snack.open(this.errorMessage(err), 'OK', { duration: 6000 });
        },
        complete: () => (this.blocking = false),
      });
    });
  }

  unblock(): void {
    if (!this.permissionChecker.hasAccessToUnblockIp()) { this.showAccessDeny(); return; }
    if (this.unblockForm.invalid) {
      this.unblockForm.markAllAsTouched();
      return;
    }
    const payload: IpBlacklistPayload = this.unblockForm.getRawValue();
    this.unblocking = true;
    this.api.unblock(payload).subscribe({
      next: () => {
        this.snack.open(`IP ${payload.ip} unblocked.`, 'OK', { duration: 4000 });
        this.unblockForm.reset();
      },
      error: (err: unknown) => {
        this.snack.open(this.errorMessage(err), 'OK', { duration: 6000 });
      },
      complete: () => (this.unblocking = false),
    });
  }

  /** Anti-footgun: refuse 0.0.0.0 + bare /0 CIDRs client-side; backend
   *  enforces the same rule but failing fast saves a round-trip. */
  private ipValidator() {
    return (ctrl: { value: string }) => {
      const v = (ctrl.value ?? '').trim();
      if (!v) return null;
      if (v === '0.0.0.0' || v.endsWith('/0')) {
        return { wildcardIp: true };
      }
      const ipv4 = /^([0-9]{1,3}\.){3}[0-9]{1,3}(\/[0-9]{1,2})?$/;
      const ipv6 = /^[0-9a-fA-F:]+(\/[0-9]{1,3})?$/;
      if (!ipv4.test(v) && !ipv6.test(v)) {
        return { invalidIp: true };
      }
      return null;
    };
  }

  private errorMessage(err: any): string {
    return err?.error?.message ?? err?.message ?? 'Operation failed';
  }
}

@Component({
  selector: 'app-confirm-ip-block-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule],
  template: `
    <h2 mat-dialog-title>Confirm block</h2>
    <mat-dialog-content>
      <p>Block requests from <strong>{{ data.ip }}</strong>?</p>
      <p class="reason">Reason: {{ data.reason }}</p>
      <p class="warn">
        This rejects all gateway traffic from this address until an admin unblocks it.
      </p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button [mat-dialog-close]="false">Cancel</button>
      <button mat-flat-button color="warn" [mat-dialog-close]="true">Block</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .reason { font-size: 0.9em; color: var(--text-muted, #666); }
    .warn { font-size: 0.85em; color: var(--warn, #c62828); margin-top: 12px; }
  `],
})
export class ConfirmIpBlockDialog {
  data: IpBlacklistPayload = inject<IpBlacklistPayload>(MAT_DIALOG_DATA);
}
